import {
  BackPriority,
  type FocusRepair,
  useBackHandler,
  useFocusWatchdog,
  useKeyMap,
} from '@akpl/ui-tv';
import { Suspense, useCallback, useEffect, useState } from 'react';

import { analytics } from '../analytics';
import { APP_VERSION } from '../api';
import { getBridge } from '../bridge';
import { useLive } from '../live';
import { useStartQueue } from '../queue/useStartQueue';
import { selectCurrent, SIGNED_OUT_SCREENS, useRouter } from '../router';
import { FULLSCREEN_SCREENS, type ScreenProps, screens } from '../screens';
import { QueueDrawer } from '../screens/Player/QueueDrawer';
import { useSongActions } from '../song/songActions';
import { selectIsInApp, useStore } from '../store';
import { useT } from '../useT';
import { AppRail } from './AppRail';
import { DevOverlay } from './DevOverlay';
import { DialogHost, ReconnectBanner, ToastHost } from './Hosts';
import { JoinQr, useJoinAnnounce } from './Join';

/**
 * Post-boot shell: rail + current screen + dialog/toast hosts + dev overlay, plus the root
 * of the Back chain (docs/04 §2): modal (Dialog) → screen handlers → here: pop the router
 * stack, or on the root show "Exit?" → `bridge.exitApp()`.
 */
export function Shell() {
  const { t } = useT();
  const route = useRouter(selectCurrent);
  const back = useRouter((s) => s.back);
  const reset = useRouter((s) => s.reset);
  const inApp = useStore(selectIsInApp);
  const openDialog = useStore((s) => s.openDialog);
  const closeDialog = useStore((s) => s.closeDialog);
  const dialogOpen = useStore((s) => s.ui.dialog !== null);
  const noteFocusRepair = useStore((s) => s.noteFocusRepair);
  const joinCode = useStore((s) => s.session?.joinCode ?? s.meta?.joinCode ?? '');
  const navigate = useRouter((s) => s.navigate);
  /**
   * UX-071: the rail's "Fila" opens the queue **over** the screen the room is looking at when
   * there is no player to open it on. The drawer lives here rather than in a screen so it can
   * outlive the one underneath it, exactly like the dialog and toast hosts.
   */
  const [queueDrawer, setQueueDrawer] = useState(false);
  // "Ya cantadas" rows in the drawer open the song dialog; playing from it opens the player.
  const { openSongActions, overlay: songOverlay } = useSongActions({
    onPlayNow: ({ itemId, songId, tone }) => {
      setQueueDrawer(false);
      navigate('player', { itemId, songId, tone });
    },
  });

  // RTDB listeners, presence and the `session_expired` guard (docs/04 §1, §5).
  useLive();

  /**
   * Exactly one focusable is focused at any time, on every screen (docs/04 §2.1, QA3 T1). The
   * watchdog lives here, above the screens, so a screen that unmounts mid-repair cannot take
   * the repair with it. Every fix is reported: a chip in the dev overlay and a `focus.repair`
   * event in Mixpanel, so a TV that keeps limping is visible instead of silent.
   */
  const onFocusRepair = useCallback(
    (repair: FocusRepair) => {
      noteFocusRepair(repair);
      analytics.track('focus.repair', {
        screen: repair.screen,
        fault: repair.fault,
        strategy: repair.strategy,
      });
    },
    [noteFocusRepair],
  );
  useFocusWatchdog({ onRepair: onFocusRepair });

  // UX-303: the room is told when somebody joins.
  useJoinAnnounce();

  // Auth gate: signed out → pairing (TV-11 is the one other signed-out screen); signed in on
  // pairing → home.
  useEffect(() => {
    if (!inApp && !SIGNED_OUT_SCREENS.includes(route.screen)) reset('pairing');
    if (inApp && route.screen === 'pairing') reset('home');
  }, [inApp, route.screen, reset]);

  useKeyMap({}, { dispatchBackChain: true });

  /**
   * UX-360: the media keys every TV remote actually has, off the player.
   *
   * With three songs in the queue and the TV idle on Home, `MediaPlay`, `MediaPlayPause` and
   * `MediaTrackNext` produced nothing at all — no screen change, no toast, not even an edge cue
   * — while the queue was three presses away inside a drawer. They now start the room's queue.
   * Not on the player: `usePlayerController` owns them there, and two handlers for one key is
   * how a play key ends up restarting the song it just paused.
   */
  const startQueue = useStartQueue();
  useKeyMap(
    { play: startQueue.start, playPause: startQueue.start, next: startQueue.start },
    { enabled: inApp && route.screen !== 'player' },
  );
  useBackHandler(
    () => {
      if (dialogOpen) {
        closeDialog();
        return true;
      }
      if (back()) return true;
      openDialog({
        id: 'exit',
        title: t('tv:home.exitTitle'),
        body: t('tv:home.exitBody'),
        actions: [
          { key: 'cancel', label: t('common:actions.cancel'), onSelect: closeDialog },
          {
            key: 'exit',
            label: t('tv:home.exitConfirm'),
            primary: true,
            onSelect: () => {
              closeDialog();
              getBridge(APP_VERSION).bridge.exitApp();
            },
          },
        ],
      });
      return true;
    },
    { priority: BackPriority.root },
  );

  const Screen = screens[route.screen] as React.ComponentType<ScreenProps>;
  const showRail = inApp && !FULLSCREEN_SCREENS.includes(route.screen);

  return (
    <>
      {showRail ? (
        <AppRail activeScreen={route.screen} onOpenQueue={() => setQueueDrawer(true)} />
      ) : null}
      <Suspense fallback={null}>
        <Screen key={route.key} params={route.params} route={route} />
      </Suspense>
      {queueDrawer && showRail ? (
        <QueueDrawer
          onClose={() => setQueueDrawer(false)}
          onSongAgain={openSongActions}
          onPlayItem={(entry) => {
            setQueueDrawer(false);
            navigate('player', { itemId: entry.id, songId: entry.songId, tone: entry.tone });
          }}
          onShowQr={() =>
            openDialog({
              id: 'join-qr',
              title: t('tv:join.title'),
              body: <JoinQr code={joinCode} />,
              actions: [
                {
                  key: 'close',
                  label: t('common:actions.close'),
                  primary: true,
                  onSelect: closeDialog,
                },
              ],
            })
          }
        />
      ) : null}
      {songOverlay}
      <DialogHost />
      <ToastHost />
      <ReconnectBanner />
      <DevOverlay />
    </>
  );
}
